import { useNavigate } from "react-router-dom";
import { m as motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import GoogleReviews from "@/components/GoogleReviews";
import PayLaterPromo from "@/components/PayLaterPromo";
import HomeInfo from "@/components/HomeInfo";
import { track, ACTIONS } from "@/lib/journey/tracker";
import { imgSet } from "@/lib/img";

export default function HomePage() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();

  const handleChoose = (c) => {
    track(c.key === "privat" ? ACTIONS.HOME_CTA_PRIVATE : ACTIONS.HOME_CTA_BUSINESS, { content_id: c.key });
    nav(c.to);
  };

  const CARDS = [
    {
      key: "privat",
      to: "/privatkunden",
      label: "01",
      eyebrow: t("home.private_eyebrow"),
      title: t("home.private_title"),
      subtitle: t("home.private_subtitle"),
      tags: [t("home.private_tag_1"), t("home.private_tag_2"), t("home.private_tag_3"), t("home.private_tag_4")],
      image: "/ext/4qnxv9ly_privat.png",
    },
    {
      key: "business",
      to: "/businesskunden",
      label: "02",
      eyebrow: t("home.business_eyebrow"),
      title: t("home.business_title"),
      subtitle: t("home.business_subtitle"),
      tags: [t("home.business_tag_1"), t("home.business_tag_2"), t("home.business_tag_3"), t("home.business_tag_4"), t("home.business_tag_5")],
      image: "/ext/2xy7ed7q_business.png",
    },
  ];

  return (
    <>
    <section
      className="relative min-h-screen w-full overflow-hidden pt-28 md:pt-32 pb-16"
      data-testid="home-selection"
    >
      {/* Layered background */}
      <div className="absolute inset-0 bg-hero-vignette" />
      <div className="absolute inset-0 pattern-luxe" />
      <div className="absolute inset-0 pattern-pinstripe opacity-60" />
      <div className="absolute inset-0 noise" />

      <div className="relative z-10 min-h-[calc(100vh-8rem)] flex flex-col justify-center container-x">
        <motion.div
          initial={{ y: 14 }}
          animate={{ y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="text-center mb-14 md:mb-20 max-w-3xl mx-auto"
        >
          <h1 className="eyebrow justify-center flex mb-8">
            <span className="gold-line" /> {t("home.h1")} <span className="gold-line" />
          </h1>
          <h2 className="heading-hero">
            {t("home.for_whom")}
          </h2>
          <p className="mt-8 max-w-xl mx-auto text-cream/65 text-base md:text-[1.05rem] leading-[1.85]">
            {t("home.for_whom_hint")}
          </p>
          <PayLaterPromo lang={i18n?.language?.startsWith("en") ? "en" : "de"} className="mt-6" />
        </motion.div>

        <div className="grid gap-6 md:gap-10 md:grid-cols-2 max-w-6xl w-full mx-auto">
          {CARDS.map((c, idx) => (
            <motion.button
              key={c.key}
              onClick={() => handleChoose(c)}
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.3 + idx * 0.15, ease: "easeOut" }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden border border-cream/[0.08] aspect-[4/5] md:aspect-[3/4] text-left transition-colors duration-700 hover:border-gold/50"
              data-testid={`home-choose-${c.key}`}
            >
              <img
                {...imgSet(c.image, "(max-width: 768px) 100vw, 50vw")}
                alt={c.title}
                width="800"
                height="1000"
                fetchpriority={idx === 0 ? "high" : undefined}
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover opacity-55 transition-all duration-[1600ms] group-hover:opacity-75 group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/85 to-ink-950/40" />
              <div className="absolute inset-0 pattern-pinstripe opacity-40 mix-blend-overlay" />

              <span className="corner-ornament tl opacity-70 group-hover:opacity-100 transition-opacity duration-700" />
              <span className="corner-ornament tr opacity-70 group-hover:opacity-100 transition-opacity duration-700" />
              <span className="corner-ornament bl opacity-70 group-hover:opacity-100 transition-opacity duration-700" />
              <span className="corner-ornament br opacity-70 group-hover:opacity-100 transition-opacity duration-700" />

              <div className="absolute top-10 left-10 right-10 flex items-center justify-between text-cream/60">
                <span className="text-[0.7rem] tracking-[0.32em] uppercase">{c.label}</span>
                <span className="flex-1 mx-6 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
              </div>

              <div className="absolute inset-x-0 bottom-0 p-10 md:p-14">
                <p className="eyebrow mb-4">{c.eyebrow}</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-[3.4rem] font-medium tracking-tight leading-[1.02] text-cream">
                  {c.title}
                </h2>
                <p className="mt-5 text-cream/75 text-[0.95rem] md:text-base max-w-md leading-relaxed">
                  {c.subtitle}
                </p>

                <div className="mt-7 flex flex-wrap gap-2">
                  {c.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[0.65rem] uppercase tracking-[0.24em] text-cream/60 border border-cream/15 px-3 py-1.5"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <span className="mt-10 inline-flex items-center gap-4 text-gold text-[0.72rem] uppercase tracking-[0.32em] transition-all group-hover:tracking-[0.38em]">
                  <span className="w-10 h-px bg-gold/70 transition-all duration-500 group-hover:w-20" />
                  {t("home.book_systems")}
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Session 32 · Echte Google-Reviews · dezenter Trust-Anker unter der Auswahl */}
        <motion.div
          initial={{ y: 24 }}
          animate={{ y: 0 }}
          transition={{ duration: 1, delay: 0.85, ease: "easeOut" }}
          className="mt-14 md:mt-20 max-w-3xl mx-auto w-full"
        >
          <GoogleReviews variant="compact" testIdPrefix="home-google-reviews" />
        </motion.div>
      </div>
    </section>
    {!i18n?.language?.startsWith("en") && <HomeInfo />}
    </>
  );
}
